import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonDelete } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { format, parseISO } from "date-fns"
import { compact, partial } from "lodash-es"
import type { PatchModel } from "@shared/models/patch"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"
import defaultPlanbookLessonsActivity from "./default-planbook-lessons-activity"
import defaultPlanbookLessonsContext from "./default-planbook-lessons-context"
import PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY from "./PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY"

interface Payload {
  courses: Array<Course>
  dateStrings: string[]
  planbookId: string
}

export function prepareEraseAllLessonsForDays(payload: Payload): PreparedAction {
  return {
    name: PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS.name,
    payload: payload,
    patches: PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS.patches(payload),
    undoPatches: PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS.undoPatches(payload),
    narrative: partial(PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS.narrative, payload),
  }
}
export const PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS = {
  name: "PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS",

  patches(payload: Payload): Array<PatchModel> {
    return payload.dateStrings.flatMap((dateString) =>
      PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY.patches({
        courses: payload.courses,
        dateString,
        planbookId: payload.planbookId,
      })
    )
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    return payload.dateStrings.flatMap((dateString) =>
      PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY.undoPatches({
        courses: payload.courses,
        dateString,
        planbookId: payload.planbookId,
      })
    )
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
    let planbook = findFn("planbook", payload.planbookId)
    let formattedStartDateString = format(parseISO(payload.dateStrings[0]), "EEE, LLL. d, yyyy")
    let formattedEndDateString = format(
      parseISO(payload.dateStrings[payload.dateStrings.length - 1]),
      "EEE, LLL. d, yyyy"
    )
    let analyticsEvents = payload.dateStrings.flatMap((dateString) =>
      pipe(
        payload.courses,
        A.filter((course) => {
          let courseDate = findCourseDateCustom(course, dateString)
          return (
            courseDate?.attributes.cardStackId !== undefined &&
            courseDate.attributes.cardStackId !== null
          )
        }),
        A.map((course): LessonDelete | null => {
          let courseDate = findCourseDateCustom(course, dateString)
          let cardStackId = courseDate?.attributes.cardStackId
          // We should never hit this because of the filter above ^
          if (cardStackId === undefined || cardStackId === null) return null
          let models = defaultLessonModels(
            { cardStackId: cardStackId, planbookId: payload.planbookId },
            findFn,
            userId
          )
          return {
            ...createBlankEvent(),
            root_id: models.planbook?.id ?? null,
            root_type: "planbook",
            primary_object_id: cardStackId,
            date_plan_scheduled_for: models.dateStringForCardStack,
            primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
            primary_verb: "delete",
            primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
            primary_object_type: "lesson",
            primary_object_specifier: null,
            adverbial_verb: "removing",
            adverbial_object_type: "lesson",
            adverbial_object_specifier: "entire_day",
            adverbial_object_id: null,
            adverbial_object_id_and_ancestor_ids: null,
          }
        }),
        (arr) => compact(arr)
      )
    )

    return {
      title: "Erased Entire Days of Lessons",
      titleForAnalytics: "Lesson - Erase Entire Day",
      description: `Erased all the lessons on ${formattedStartDateString} - ${formattedEndDateString} and removed their lesson histories`,
      context: defaultPlanbookLessonsContext(planbook, payload),
      activity: defaultPlanbookLessonsActivity(payload.planbookId, userId),
      newAnalyticsEvents: analyticsEvents,
    }
  },
}
export default PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS
